import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useSlip } from "@/contexts/SlipContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronUp, X, Trash2, Sparkles, ClipboardCopy, Calculator } from "lucide-react";
import { toast } from "sonner";

const CHOICE_LABEL = { HOME: "1", DRAW: "X", AWAY: "2" };

const RISK_COLORS = {
    SALAMA: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    WASTANI: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
    HATARI: "bg-red-500/20 text-red-400 border-red-500/40",
};

const SlipDrawer = () => {
    const { picks, removePick, setChoice, clearSlip } = useSlip();
    const [open, setOpen] = useState(false);
    const [computing, setComputing] = useState(false);
    const [result, setResult] = useState(null);

    if (picks.length === 0) return null;

    const compute = async () => {
        setComputing(true);
        setResult(null);
        try {
            const r = await axios.post(`${API}/predictions/parlay`, {
                picks: picks.map((p) => ({
                    home_team: p.home_team,
                    away_team: p.away_team,
                    league_id: p.league_id,
                    event_id: p.event_id,
                    kickoff_iso: p.kickoff_iso,
                    choice: p.choice,
                })),
            }, { timeout: 120000 });
            setResult(r.data);
            setOpen(true);
        } catch (e) {
            toast.error("Imeshindikana kuhesabu mkeka. Jaribu tena.");
        } finally {
            setComputing(false);
        }
    };

    const copyTagline = () => {
        const t = result?.consensus?.sms_tagline || "";
        if (!t) return;
        navigator.clipboard?.writeText(t);
        toast.success("Imenakiliwa!");
    };

    return (
        <>
            {/* Sticky launcher (when closed) */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed bottom-4 right-4 z-40 bg-electric text-white shadow-2xl rounded-full pl-4 pr-3 py-3 flex items-center gap-3 hover:scale-105 transition-transform"
                    data-testid="slip-launcher"
                >
                    <span className="font-heading font-black uppercase tracking-wider text-sm">Mkeka Wangu</span>
                    <span className="bg-white text-electric font-mono font-black text-sm rounded-full h-7 w-7 flex items-center justify-center">
                        {picks.length}
                    </span>
                    <ChevronUp className="h-4 w-4" />
                </button>
            )}

            {/* Open drawer */}
            {open && (
                <div className="fixed bottom-0 right-0 left-0 sm:left-auto sm:right-4 sm:bottom-4 z-40 w-full sm:w-[420px] max-h-[85vh] bg-paper border border-electric/40 sm:rounded-lg shadow-2xl flex flex-col" data-testid="slip-drawer">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-electric text-white sm:rounded-t-lg">
                        <div className="flex items-center gap-2">
                            <span className="font-heading font-black uppercase tracking-wider text-sm">Mkeka Wangu</span>
                            <Badge className="bg-white text-electric font-mono">{picks.length}/8</Badge>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={clearSlip} title="Futa zote" className="p-1.5 hover:bg-white/10 rounded" data-testid="slip-clear">
                                <Trash2 className="h-4 w-4" />
                            </button>
                            <button onClick={() => setOpen(false)} title="Funga" className="p-1.5 hover:bg-white/10 rounded" data-testid="slip-close">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Result Banner */}
                    {result && result.consensus && (
                        <div className="px-4 py-3 bg-subtle border-b border-border" data-testid="slip-result-banner">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-white/40 text-xs uppercase tracking-wider">Uwezekano Pamoja</span>
                                <Badge className={`text-xs font-bold ${RISK_COLORS[result.consensus.risk?.label] || ""}`}>
                                    {result.consensus.risk?.label}
                                </Badge>
                            </div>
                            <div className="flex items-baseline gap-3">
                                <span className="font-heading font-black text-3xl text-electric">
                                    {result.consensus.user_combined_probability}%
                                </span>
                                <span className="text-white/60 text-sm">
                                    Odds halali: <span className="text-white font-mono font-bold">{result.consensus.user_fair_odds}x</span>
                                </span>
                            </div>
                            <p className="text-white/50 text-xs mt-1 leading-relaxed">{result.consensus.risk?.explanation}</p>
                            <button
                                onClick={copyTagline}
                                className="mt-2 inline-flex items-center gap-1 text-electric hover:text-white text-xs font-medium"
                                data-testid="slip-copy-tagline"
                            >
                                <ClipboardCopy className="h-3 w-3" />
                                Nakili kwa SMS
                            </button>
                        </div>
                    )}

                    {/* Pick list */}
                    <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                        {picks.map((p, idx) => {
                            const r = result?.picks?.find(
                                (rp) => rp.home_team === p.home_team && rp.away_team === p.away_team
                            );
                            return (
                                <div key={p.id} className="bg-subtle border border-border rounded-md p-3" data-testid={`slip-pick-${idx}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-white/40 text-[10px] uppercase tracking-wider truncate flex items-center gap-1">
                                            <span>{p.league_flag}</span>
                                            <span>{p.league_name_sw}</span>
                                        </span>
                                        <button
                                            onClick={() => removePick(p.id)}
                                            className="text-white/30 hover:text-red-400"
                                            data-testid={`slip-remove-${idx}`}
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                    <div className="text-white text-sm font-medium leading-tight mb-2">
                                        {p.home_team} <span className="text-white/30">vs</span> {p.away_team}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {["HOME", "DRAW", "AWAY"].map((c) => (
                                            <button
                                                key={c}
                                                onClick={() => setChoice(p.id, c)}
                                                className={`flex-1 py-1.5 text-xs font-bold rounded transition-colors ${
                                                    p.choice === c
                                                        ? "bg-electric text-white"
                                                        : "bg-white/5 text-white/40 hover:text-white"
                                                }`}
                                                data-testid={`slip-choice-${idx}-${c}`}
                                            >
                                                {c === "HOME" ? p.home_team.split(" ")[0].slice(0, 8) : c === "AWAY" ? p.away_team.split(" ")[0].slice(0, 8) : "Sare"}
                                                <span className="text-[9px] opacity-60 ml-1">({CHOICE_LABEL[c]})</span>
                                            </button>
                                        ))}
                                    </div>
                                    {r && (
                                        <div className="mt-2 pt-2 border-t border-border flex items-center justify-between text-[11px]">
                                            <span className="text-white/50">
                                                AI: <span className="text-electric font-bold">{r.winner}</span> {r.confidence}%
                                            </span>
                                            <span className={`font-mono font-bold ${
                                                r.user_choice === r.verdict ? "text-emerald-400" : "text-yellow-400"
                                            }`}>
                                                {r.user_choice_prob}%
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-border p-3">
                        <Button
                            onClick={compute}
                            disabled={computing || picks.length === 0}
                            className="w-full bg-electric hover:bg-electric/80 text-white font-heading font-black uppercase tracking-wider"
                            data-testid="slip-compute-btn"
                        >
                            {computing ? (
                                <><Sparkles className="h-4 w-4 mr-2 animate-spin" /> Wataalamu wanachambua...</>
                            ) : (
                                <><Calculator className="h-4 w-4 mr-2" /> Hesabu Mkeka</>
                            )}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default SlipDrawer;
